import React from 'react';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Points from 'components/Points';
import { ListGroup } from 'store/services/collectionList/types';
import { COLOR_PIPS } from './CardFilterTokenRow';
import { GroupKind } from './useGroupedRows';
import './GroupHeaderRow.css';

interface GroupHeaderRowProps {
  group: ListGroup;
  kind: GroupKind;
  collapsed: boolean;
  onToggle: () => void;
  /** 'table-row' renders a full-width <tr> for CardsTable; 'block' spans an image grid. */
  as: 'table-row' | 'block';
  /** Column count, table-row only. */
  colSpan?: number;
}

const formatCount = (n: number, noun: string) =>
  `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;

const GroupGlyph: React.FC<{ group: ListGroup; kind: GroupKind }> = ({ group, kind }) => {
  if (kind === 'set') {
    return <span className="group-header-set-code">{group.key.toUpperCase()}</span>;
  }
  const pip = COLOR_PIPS[group.key];
  if (pip) return <img className="group-header-pip" src={pip.icon} alt="" aria-hidden="true" />;
  // Multicolor and Land have no single mana symbol.
  return <span className={`group-header-letter group-header-letter--${group.key.toLowerCase()}`}>{group.key}</span>;
};

/**
 * Collapsible bucket header for color- and set-sorted lists (decision 8):
 * glyph, bucket name, card count, copy count, point total. Sticky under the
 * table header while scrolling (CardsTable scopes that to grouped tables).
 */
export const GroupHeaderRow: React.FC<GroupHeaderRowProps> = ({
  group,
  kind,
  collapsed,
  onToggle,
  as,
  colSpan = 1,
}) => {
  const content = (
    <button
      type="button"
      className="group-header-button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.label}`}
    >
      <span className={`group-header-chevron${collapsed ? ' collapsed' : ''}`} aria-hidden="true" />
      <GroupGlyph group={group} kind={kind} />
      <span className="group-header-label">{group.label}</span>
      <span className="group-header-counts">
        {formatCount(group.cardCount, 'card')} · {formatCount(group.copyCount, 'cop').replace(/cops?$/, group.copyCount === 1 ? 'copy' : 'copies')}
      </span>
      <span className="group-header-total">
        <Points points={group.pointTotal} type="small" />
      </span>
    </button>
  );

  if (as === 'block') {
    return (
      <div className="group-header-row group-header-row--block" data-testid="group-header" data-group-key={group.key}>
        {content}
      </div>
    );
  }

  return (
    <TableRow className="group-header-row cards-table-group-row" data-testid="group-header" data-group-key={group.key}>
      <TableCell colSpan={colSpan} className="group-header-cell">
        {content}
      </TableCell>
    </TableRow>
  );
};

export default GroupHeaderRow;
